import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { getExpediente, updateExpediente } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FloppyDisk, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import DatosTab from "@/components/expediente/DatosTab";
import DocumentosTab from "@/components/expediente/DocumentosTab";
import FotosTab from "@/components/expediente/FotosTab";
import ResultadosTab from "@/components/expediente/ResultadosTab";
import TimelineTab from "@/components/expediente/TimelineTab";
import PlantillasTab from "@/components/expediente/PlantillasTab";
import ActaTab from "@/components/expediente/ActaTab";
import DuplicadoTab from "@/components/expediente/DuplicadoTab";
import PosesionTab from "@/components/expediente/PosesionTab";
import PoderTab from "@/components/expediente/PoderTab";

export default function ExpedienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { role, can } = useAuth();
  const isCampo = role === "campo";
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(isCampo ? "acta" : "datos");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getExpediente(id);
      setExp(data);
    } catch (e) { toast.error(t.error); navigate("/"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveAll = async () => {
    if (!exp) return;
    setSaving(true);
    try {
      let payload;
      if (isCampo) {
        // Solo Acta + Fotos para acceso de campo
        payload = {
          survey_results: exp.survey_results,
        };
      } else {
        payload = {
          numero_expediente: exp.numero_expediente,
          titulo_expediente: exp.titulo_expediente,
          tipo_tramite: exp.tipo_tramite,
          tipo_operacion: exp.tipo_operacion,
          drmc_departamento: exp.drmc_departamento,
          tribunal_jurisdiccion: exp.tribunal_jurisdiccion,
          cliente: exp.cliente,
          notario: exp.notario,
          acto_venta: exp.acto_venta,
          titulo: exp.titulo,
          terreno: exp.terreno,
          survey_results: exp.survey_results,
          duplicado: exp.duplicado,
          posesion: exp.posesion,
          poder: exp.poder,
          timeline: exp.timeline,
          custom_clauses: exp.custom_clauses || {},
        };
        // Solo admin envía honorarios
        if (can("seeHonorarios")) payload.honorarios = exp.honorarios;
      }
      const updated = await updateExpediente(id, payload);
      setExp(updated);
      toast.success(t.saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.error);
    }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-slate-500 font-mono text-sm">Loading…</div>;
  if (!exp) return null;

  const progressDone = (exp.timeline || []).filter(s => s.completed).length;
  const progressTotal = exp.timeline?.length || 9;
  const pct = Math.round((progressDone / progressTotal) * 100);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="expediente-detail-page">
      <button
        data-testid="back-btn"
        onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-5"
      >
        <ArrowLeft size={14} /> {t.back}
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 border-b border-slate-200 pb-6">
        <div>
          <div className="coord-label mb-1.5">EXP. #{exp.numero_expediente}</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
            {exp.titulo_expediente || exp.cliente?.nombre || t.new_expediente}
          </h1>
          <div className="mt-3 flex items-center gap-3 max-w-md">
            <div className="flex-1 h-1.5 bg-slate-200 rounded-sm overflow-hidden">
              <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono text-xs text-slate-600">{progressDone}/{progressTotal} • {pct}%</span>
          </div>
          {isCampo && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm">
              <Lock size={12} /> Acceso de Campo — solo Acta de Hitos y Fotos son editables
            </div>
          )}
        </div>
        <Button
          data-testid="save-all-btn"
          onClick={saveAll}
          disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
        >
          <FloppyDisk weight="duotone" size={16} className="mr-1.5" />
          {saving ? "…" : t.save}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto w-full justify-start gap-1 overflow-x-auto scrollbar-thin">
          <TabsTrigger value="datos" data-testid="tab-datos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">{t.tab_datos}</TabsTrigger>
          {exp.tipo_tramite === "duplicado_perdida" && (
            <TabsTrigger value="duplicado" data-testid="tab-duplicado" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">Duplicado</TabsTrigger>
          )}
          {exp.tipo_tramite === "posesion_mejora" && (
            <TabsTrigger value="posesion" data-testid="tab-posesion" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">Posesión</TabsTrigger>
          )}
          <TabsTrigger value="poder" data-testid="tab-poder" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">Poder</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">{t.tab_documentos}</TabsTrigger>
          {can("editTemplates") && (
            <TabsTrigger value="plantillas" data-testid="tab-plantillas" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">Plantillas</TabsTrigger>
          )}
          {exp.tipo_tramite !== "duplicado_perdida" && exp.tipo_tramite !== "posesion_mejora" && (
            <>
              <TabsTrigger value="fotos" data-testid="tab-fotos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">{t.tab_fotos}</TabsTrigger>
              <TabsTrigger value="resultados" data-testid="tab-resultados" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">{t.tab_resultados}</TabsTrigger>
              <TabsTrigger value="acta" data-testid="tab-acta" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">Acta de Hitos</TabsTrigger>
            </>
          )}
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-semibold">{t.tab_timeline}</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="datos">
            <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
              <DatosTab exp={exp} setExp={setExp} reload={load} />
            </fieldset>
          </TabsContent>
          {exp.tipo_tramite === "duplicado_perdida" && (
            <TabsContent value="duplicado">
              <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
                <DuplicadoTab exp={exp} setExp={setExp} />
              </fieldset>
            </TabsContent>
          )}
          {exp.tipo_tramite === "posesion_mejora" && (
            <TabsContent value="posesion">
              <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
                <PosesionTab exp={exp} setExp={setExp} />
              </fieldset>
            </TabsContent>
          )}
          <TabsContent value="poder">
            <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
              <PoderTab exp={exp} setExp={setExp} />
            </fieldset>
          </TabsContent>
          <TabsContent value="documentos"><DocumentosTab exp={exp} /></TabsContent>
          {can("editTemplates") && (
            <TabsContent value="plantillas"><PlantillasTab exp={exp} setExp={setExp} /></TabsContent>
          )}
          {exp.tipo_tramite !== "duplicado_perdida" && exp.tipo_tramite !== "posesion_mejora" && (
            <>
              <TabsContent value="fotos"><FotosTab exp={exp} setExp={setExp} reload={load} /></TabsContent>
              <TabsContent value="resultados">
                <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
                  <ResultadosTab exp={exp} setExp={setExp} />
                </fieldset>
              </TabsContent>
              <TabsContent value="acta"><ActaTab exp={exp} setExp={setExp} /></TabsContent>
            </>
          )}
          <TabsContent value="timeline">
            <fieldset disabled={isCampo} className={isCampo ? "opacity-90" : ""}>
              <TimelineTab exp={exp} setExp={setExp} />
            </fieldset>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
